import { useEffect, useState } from "react";
import { LineChart, TrendingDown, FileCheck2, Sparkles, LifeBuoy } from "lucide-react";
import AppShell from "@/components/AppShell";
import { FullPageLoader } from "@/components/Common";
import { api } from "@/lib/api";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-brand-line bg-white p-4 shadow-soft">
      <div className="flex items-center gap-2 text-slate-500">
        <Icon size={15} className="text-brand" strokeWidth={1.8} />
        <p className="text-sm">{label}</p>
      </div>
      <p className="mt-1 font-heading text-2xl font-semibold">{value}</p>
    </div>
  );
}

export default function FounderAnalytics() {
  const [data, setData] = useState(null);
  const [beta, setBeta] = useState(null);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    api.get("/founder/analytics").then(({ data }) => setData(data)).catch(() => setData({ error: true }));
    api.get("/founder/beta-metrics").then(({ data }) => setBeta(data)).catch(() => {});
    api.get("/founder/feedback").then(({ data }) => setFeedback(data)).catch(() => {});
  }, []);

  if (!data) return <FullPageLoader label="Crunching the numbers…" />;
  if (data.error) return <AppShell><div className="p-10 text-slate-600">Could not load analytics.</div></AppShell>;

  const maxStall = Math.max(1, ...data.stall_by_step.map((s) => s.count));

  return (
    <AppShell>
      <div className="thin-scroll mx-auto max-w-5xl px-8 py-10 ltifade" data-testid="founder-analytics">
        <div className="flex items-center gap-2">
          <LineChart size={20} className="text-brand" />
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Analytics</h1>
        </div>
        <p className="mt-1 text-slate-500">From first signup to first sale, and where people get stuck.</p>

        {/* Closed beta monitoring */}
        {beta && (
          <div className="mt-6" data-testid="beta-metrics">
            <h3 className="font-heading text-lg font-medium">Closed beta monitoring</h3>
            <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Stat icon={LineChart} label="Activation" value={`${beta.rates.activation}%`} />
              <Stat icon={FileCheck2} label="First task done" value={`${beta.rates.first_task}%`} />
              <Stat icon={Sparkles} label="AI drafts" value={beta.ai.drafts} />
              <Stat icon={LifeBuoy} label="Feedback" value={beta.support_feedback_count} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Stat icon={FileCheck2} label="Assets created" value={beta.assets_created} />
              <Stat icon={LineChart} label="Launched" value={beta.funnel.launched} />
              <Stat icon={LineChart} label="First sales" value={beta.funnel.first_sale} />
              <Stat icon={Sparkles} label="AI cost (est.)" value={`$${beta.ai.cost_estimate_usd}`} />
            </div>
            {feedback && (
              <div className="mt-3 flex flex-wrap items-center gap-4 rounded-xl border border-brand-line bg-white p-4 text-sm shadow-soft">
                <span className="text-slate-600">Avg rating <span className="font-semibold text-slate-800">{feedback.avg_rating ?? "n/a"}</span></span>
                <span className="text-slate-300">|</span>
                <span className="text-slate-600">Would pay: <span className="font-medium text-emerald-600">{feedback.pricing_interest.yes} yes</span>, {feedback.pricing_interest.maybe} maybe, {feedback.pricing_interest.no} no</span>
                {beta.ai.errors > 0 && <><span className="text-slate-300">|</span><span className="text-amber-600">{beta.ai.errors} AI errors</span></>}
              </div>
            )}
          </div>
        )}

        {/* Funnel */}
        <h3 className="mt-8 font-heading text-lg font-medium">The journey to first income</h3>
        <div className="mt-3 space-y-2.5 rounded-xl border border-brand-line bg-white p-5 shadow-soft" data-testid="funnel">
          {data.funnel.map((f, i) => (
            <div key={f.stage} className="flex items-center gap-3" data-testid={`funnel-stage-${i}`}>
              <span className="w-40 shrink-0 text-sm text-slate-600">{f.stage}</span>
              <div className="relative h-8 flex-1 overflow-hidden rounded-lg bg-brand-surface/50">
                <div className="flex h-full items-center rounded-lg bg-brand px-3 text-xs font-semibold text-white transition-all duration-500" style={{ width: `${Math.max(f.conversion, 6)}%` }}>
                  {f.count}
                </div>
              </div>
              <span className="w-12 shrink-0 text-right text-sm font-medium text-slate-500">{f.conversion}%</span>
            </div>
          ))}
        </div>

        {/* Bottleneck callout */}
        {data.bottleneck && data.bottleneck.count > 0 && (
          <div className="mt-4 flex items-start gap-3 rounded-xl border-2 border-amber-200 bg-amber-50/50 p-4" data-testid="bottleneck">
            <TrendingDown size={18} className="mt-0.5 text-amber-600" />
            <p className="text-sm text-slate-700">
              Most students are stuck at <span className="font-semibold">{data.bottleneck.title}</span> ({data.bottleneck.count} {data.bottleneck.count === 1 ? "student" : "students"}, avg {data.bottleneck.avg_days} days). This is your biggest opportunity to help.
            </p>
          </div>
        )}

        {/* Where students stall */}
        <h3 className="mt-8 font-heading text-lg font-medium">Where students stall</h3>
        <div className="mt-3 space-y-2 rounded-xl border border-brand-line bg-white p-5 shadow-soft" data-testid="stall-by-step">
          {data.stall_by_step.map((s) => (
            <div key={s.slug} className="flex items-center gap-3">
              <span className="w-6 text-sm text-slate-400">{s.order}</span>
              <span className="w-48 shrink-0 truncate text-sm text-slate-600">{s.title}</span>
              <div className="h-5 flex-1 overflow-hidden rounded-md bg-brand-surface/40">
                <div className="h-full rounded-md bg-brand-muted transition-all duration-500" style={{ width: `${(s.count / maxStall) * 100}%` }} />
              </div>
              <span className="w-24 shrink-0 text-right text-xs text-slate-400">
                {s.count} {s.count === 1 ? "student" : "students"}{s.count ? ` · ${s.avg_days}d` : ""}
              </span>
            </div>
          ))}
        </div>

        {/* Activity */}
        <h3 className="mt-8 font-heading text-lg font-medium">Activity</h3>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Stat icon={FileCheck2} label="Assets built" value={data.activity.assets_built} />
          <Stat icon={Sparkles} label="AI drafts used" value={data.activity.ai_drafts} />
          <Stat icon={LifeBuoy} label="Interventions" value={data.activity.interventions} />
        </div>
      </div>
    </AppShell>
  );
}
